/**
 * Login Component
 * Handles user authentication
 */

import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  TextField,
  Typography,
  Alert,
  CircularProgress,
  Link as MuiLink,
  Container
} from "@mui/material";
import { loginUser } from "../services/api";

const Login = ({ onLoginSuccess, onNavigateToSignup, prefilledEmail = "" }) => {
  const [formData, setFormData] = useState({
    email: prefilledEmail,
    password: ""
  });

  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState({ type: "", text: "" });

  // Update email if prefilledEmail changes (e.g., after signup)
  useEffect(() => {
    if (prefilledEmail) {
      setFormData((prev) => ({ ...prev, email: prefilledEmail }));
    }
  }, [prefilledEmail]);

  // Handle input changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value
    }));
    // Clear error for this field
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: ""
      }));
    }
  };

  // Validate form
  const validateForm = () => {
    const newErrors = {};

    if (!formData.email) {
      newErrors.email = "Email is required";
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = "Email is invalid";
    }

    if (!formData.password) {
      newErrors.password = "Password is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsLoading(true);
    setMessage({ type: "", text: "" });

    if (window.grecaptcha && window.grecaptcha.enterprise) {
      const recaptchaToken = await window.grecaptcha.enterprise.execute(
        "6LdLSforAAAAAMrgvpCE4tIvO4tqQVToFtN6hsSm",
        { action: "login" }
      );
      formData.recaptcha = recaptchaToken;
    }

    try {
      const result = await loginUser(
        formData.email,
        formData.password,
        formData.recaptcha
      );

      if (result.success) {
        setMessage({
          type: "success",
          text: "Login successful! Redirecting..."
        });

        // Store user token/session
        if (result.token) {
          localStorage.setItem("userToken", result.token);
          localStorage.setItem("userEmail", formData.email);
        }

        // Redirect after 1 second
        setTimeout(() => {
          if (onLoginSuccess) {
            onLoginSuccess(result.user, result.token);
          }
        }, 1000);
      } else {
        setMessage({
          type: "error",
          text: result.message || "Login failed"
        });
      }
    } catch (error) {
      setMessage({
        type: "error",
        text: "Invalid email or password"
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Container maxWidth="sm">
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          py: 4
        }}
      >
        <Card sx={{ width: "100%", boxShadow: 3 }}>
          <CardContent sx={{ p: 4 }}>
            <Typography variant="h4" component="h2" align="center" gutterBottom>
              Welcome Back
            </Typography>
            <Typography
              variant="body1"
              color="text.secondary"
              align="center"
              sx={{ mb: 3 }}
            >
              Sign in to your account
            </Typography>

            {message.text && (
              <Alert severity={message.type} sx={{ mb: 3 }}>
                {message.text}
              </Alert>
            )}

            <Box component="form" onSubmit={handleSubmit} noValidate>
              <TextField
                fullWidth
                label="Email Address"
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                error={!!errors.email}
                helperText={errors.email}
                placeholder="Enter your email"
                autoComplete="email"
                margin="normal"
                required
              />

              <TextField
                fullWidth
                label="Password"
                type="password"
                id="password"
                name="password"
                value={formData.password}
                onChange={handleChange}
                error={!!errors.password}
                helperText={errors.password}
                placeholder="Enter your password"
                autoComplete="current-password"
                margin="normal"
                required
              />

              <Button
                fullWidth
                type="submit"
                variant="contained"
                size="large"
                disabled={isLoading}
                sx={{ mt: 3, mb: 2 }}
              >
                {isLoading ? (
                  <>
                    <CircularProgress
                      size={24}
                      sx={{ mr: 1 }}
                      color="inherit"
                    />
                    Signing in...
                  </>
                ) : (
                  "Sign In"
                )}
              </Button>
            </Box>

            <Box sx={{ mt: 2, textAlign: "center" }}>
              <Typography variant="body2">
                Don't have an account?{" "}
                <MuiLink
                  component="button"
                  variant="body2"
                  onClick={onNavigateToSignup}
                  sx={{ cursor: "pointer" }}
                >
                  Sign up here
                </MuiLink>
              </Typography>
            </Box>
          </CardContent>
        </Card>
      </Box>
    </Container>
  );
};

export default Login;
